import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

function money(cents, currency = "usd") {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(cents / 100);
}

const FEATURES = {
  plan_starter: ["25 researched leads delivered weekly", "LinkedIn + headcount enrichment", "Draft emails & LinkedIn notes", "Email list to your clients", "Email support"],
  plan_growth:  ["125 researched leads delivered weekly", "Everything in Starter", "Bulk re-enrich", "Reported-lead moderation", "Priority support"],
  plan_scale:   ["500 researched leads delivered weekly", "Everything in Growth", "Dedicated success manager", "Early access to new AI models", "Private Slack channel"],
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(null);

  useEffect(() => { api.get("/payments/plans").then(({ data }) => setPlans(data)); }, []);

  const subscribe = async (plan_key) => {
    setLoading(plan_key);
    try {
      const { data } = await api.post("/payments/subscribe", { plan_key, interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline text-primary">Plans</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-medium tracking-tighter">Pick a plan that scales with you.</h1>
          <p className="mt-5 text-muted-foreground font-light leading-relaxed">
            Every plan is a monthly recurring subscription with the full feature set. Switch, upgrade or cancel anytime.
          </p>

          <div className="mt-8 inline-flex items-center rounded-full border border-border bg-[#121214] p-1" data-testid="interval-toggle">
            {[["month", "Monthly"], ["year", "Annual · save 17%"]].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setInterval(v)}
                className={`px-5 py-2 text-sm rounded-full transition-colors ${interval === v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`interval-${v}`}
              >{label}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {plans.map((p, i) => {
            const price = p.prices?.[interval];
            const monthlyEquiv = interval === "year" && price ? price.amount / 12 : null;
            return (
              <div
                key={p.plan_key}
                className={`rounded-2xl border bg-[#121214] p-8 relative ${i === 1 ? "tracing-border border-primary/40" : "border-border"}`}
                data-testid={`plan-card-${p.plan_key}`}
              >
                {i === 1 && (
                  <div className="absolute -top-3 left-8 text-xs px-3 py-1 rounded-full bg-primary text-white uppercase tracking-[0.18em] font-medium">
                    Most popular
                  </div>
                )}
                <div className="overline text-primary">{p.name}</div>
                <div className="mt-1 text-sm text-muted-foreground">{p.tagline}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <div className="text-5xl font-medium tracking-tighter">{price ? money(price.amount) : "—"}</div>
                  <div className="text-sm text-muted-foreground">/ {interval === "month" ? "mo" : "yr"}</div>
                </div>
                {monthlyEquiv && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {money(Math.round(monthlyEquiv))}/mo billed annually
                  </div>
                )}
                <div className="mt-4 text-sm text-primary">
                  {p.weekly_leads.toLocaleString()} researched leads per week
                  <span className="text-muted-foreground"> · {p.monthly_leads.toLocaleString()} / month</span>
                </div>
                {p.trial_days > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid={`trial-badge-${p.plan_key}`}>
                    {p.trial_days}-day free trial
                  </div>
                )}
                <ul className="mt-8 space-y-3">
                  {(FEATURES[p.plan_key] || []).map((f) => (
                    <li key={f} className="text-sm flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(p.plan_key)}
                  disabled={loading === p.plan_key || !price}
                  className={`w-full mt-8 rounded-full h-12 ${i === 1 ? "bg-primary hover:bg-[#FF7043] text-white" : ""}`}
                  variant={i === 1 ? "default" : "outline"}
                  data-testid={`subscribe-${p.plan_key}`}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {loading === p.plan_key ? "Opening checkout…" : `Choose ${p.name}`}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-10">
          Test card: 4242 4242 4242 4242 · any future expiry · any CVC
        </p>
      </main>
    </div>
  );
}
